import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, TextArea, Banner, useToast } from '@morressier/alembic';

import { FieldLabel } from 'src/components/Field';
import { FieldError, FormProvider, useForm } from 'react-hook-form';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import _ from 'lodash';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import {
  FormFieldFileUploadArray,
  FormFieldTextInput
} from '@publishing-engine/domain/types/article';
import { IDecisionModalProps } from './decisionModalHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FormFieldMultipleFileUpload } from 'src/components/FormField';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useState } from 'react';

export type IAuthorResponseValue = {
  authorResponse?: string;
  authorResponseAttachments?: ISubmissionFile[];
};

const AuthorResponseUpdateModal = ({
  onClose,
  submissionId,
  submissionContextId
}: IDecisionModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const { getFieldState, getOnChangeHandler } = useSubmissionFields();
  const { activeRevision, refetchSubmission } = useSubmissionData();
  const authorResponseField =
    getFieldState<FormFieldTextInput<'authorResponse'>>('AuthorResponse')!;
  const authorResponseAttachmentsField = getFieldState<
    FormFieldFileUploadArray<'authorResponseAttachments'>
  >('AuthorResponseAttachments');

  const {
    formFieldDefinition: authorResponseFFD,
    value: authorResponseValue = ''
  } = authorResponseField ?? {};

  const { formFieldDefinition: authorAttachmentsFFD } =
    authorResponseAttachmentsField ?? {};

  const authorResponseOnChange = getOnChangeHandler('AuthorResponse');
  const authorResponseAttachmentsOnChange = getOnChangeHandler(
    'AuthorResponseAttachments'
  );
  const schema = generateValidationSchema({
    authorResponse: authorResponseFFD || null,
    authorResponseAttachments: authorAttachmentsFFD || null
  });
  const authorResponseFieldName = authorResponseFFD?.caseDataFields.main;
  const authorAttachmentsFieldName = authorAttachmentsFFD?.caseDataFields.main;

  const methods = useForm({
    defaultValues: _.merge(
      {},
      { [authorResponseFieldName]: activeRevision?.authorResponse ?? '' },
      authorAttachmentsFieldName
        ? {
            [authorAttachmentsFieldName]:
              activeRevision?.authorResponseAttachments ?? []
          }
        : {}
    ),
    resolver: ajvResolver(schema)
  });

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors }
  } = methods;
  const error = _.get(errors, authorResponseFieldName) as FieldError;

  const onSubmit = async (data: IAuthorResponseValue) => {
    setIsLoading(true);
    await authorResponseOnChange?.(data.authorResponse, false);
    await authorResponseAttachmentsOnChange?.(
      data.authorResponseAttachments,
      false
    );
    setIsLoading(false);
    refetchSubmission();

    toast({
      variant: 'success',
      title: 'Response Saved',
      description: 'Your response is ready to submit to the Editors.'
    });
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex flexDirection="column" gap={8}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection="column" gap={8} padding={8} width="100%">
              <Flex>
                <FontAwesomeIcon fontSize={32} icon={faEnvelope} />
              </Flex>
              <Flex flexDirection="column" gap={2}>
                {authorResponseFFD && (
                  <>
                    <Flex flexDirection="column" gap={4}>
                      <Text textStyle="headingGroup">Write Your Response</Text>
                      <Banner
                        variant="attention"
                        heading="Not Sent Yet."
                        content="Editors won't see your response until you submit your revision."
                      />
                    </Flex>
                    <Flex
                      flexDirection="column"
                      gap={4}
                      alignItems="flex-start"
                    >
                      <FieldLabel
                        label={authorResponseFFD.params.labels.main}
                        subText={authorResponseFFD.params.labels.subtitle}
                        isRequired={
                          authorResponseFFD.params.validations.required
                        }
                      />
                      <Flex flex={1} width={'100%'} gap={4}>
                        <TextArea
                          height={`${authorResponseValue?.length * 0.75}px`}
                          value={watch(authorResponseFieldName)}
                          {...register(authorResponseFieldName)}
                          isInvalid={Boolean(error)}
                          errorMessage={
                            Boolean(error) &&
                            `Please enter a ${authorResponseFFD.params.labels.main}`
                          }
                          minHeight={100}
                          maxHeight={600}
                        />
                      </Flex>
                    </Flex>
                  </>
                )}
                {authorAttachmentsFFD && (
                  <FormFieldMultipleFileUpload
                    submissionId={submissionId}
                    submissionContextId={submissionContextId}
                    config={authorAttachmentsFFD}
                  />
                )}
              </Flex>

              <Flex gap={2} justifyContent="flex-end">
                <Button
                  variant="secondary"
                  onClick={onClose}
                  borderRadius="small"
                  isDisabled={isLoading}
                >
                  Exit Without Saving
                </Button>
                <Button
                  isLoading={isLoading}
                  variant="primary"
                  type="submit"
                  borderRadius="small"
                >
                  Save Changes
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Flex>
    </Modal>
  );
};

export { AuthorResponseUpdateModal };
